import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
interface Contact {
  id: number;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: string;
  tags: string[];
  connectedOn: string;
  linkedin: string;
  followUp: string;
  notes: string;
}
interface EditContactModalProps {
  contact: Contact | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: Contact) => void;
}
const AVAILABLE_TAGS = ['Opener', 'Erstnachricht', 'Interessiert', 'Kunde', 'Follow-up', 'Überfällig', 'Disqualifiziert'];
export function EditContactModal({
  contact,
  isOpen,
  onClose,
  onSave
}: EditContactModalProps) {
  const [formData, setFormData] = useState<Contact | null>(null);
  useEffect(() => {
    if (contact) {
      setFormData({
        ...contact
      });
    }
  }, [contact]);
  if (!isOpen || !formData) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };
  const handleChange = (field: keyof Contact, value: string) => {
    setFormData(prev => prev ? {
      ...prev,
      [field]: value
    } : null);
  };
  const handleTagToggle = (tag: string) => {
    setFormData(prev => {
      if (!prev) return null;
      const isSelected = prev.tags.includes(tag);
      return {
        ...prev,
        tags: isSelected ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag]
      };
    });
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-79">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-80" />

      {/* Modal */}
      <Card className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border" data-id="element-81">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between" data-id="element-82">
          <div data-id="element-83">
            <h2 className="text-xl font-semibold text-foreground" data-id="element-84">
              Kontakt bearbeiten
            </h2>
            <p className="text-sm text-muted-foreground mt-1" data-id="element-85">
              Aktualisieren Sie die Kontaktinformationen
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8" data-id="element-86">
            <X className="h-4 w-4" data-id="element-87" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-88">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-89">
            <div className="space-y-2" data-id="element-90">
              <Label htmlFor="name" data-id="element-91">Name *</Label>
              <Input id="name" value={formData.name} onChange={e => handleChange('name', e.target.value)} required data-id="element-92" />
            </div>

            <div className="space-y-2" data-id="element-93">
              <Label htmlFor="company" data-id="element-94">Unternehmen</Label>
              <Input id="company" value={formData.company} onChange={e => handleChange('company', e.target.value)} data-id="element-95" />
            </div>

            <div className="space-y-2" data-id="element-96">
              <Label htmlFor="email" data-id="element-97">E-Mail *</Label>
              <Input id="email" type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} required data-id="element-98" />
            </div>

            <div className="space-y-2" data-id="element-99">
              <Label htmlFor="phone" data-id="element-100">Telefon</Label>
              <Input id="phone" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} data-id="element-101" />
            </div>

            <div className="space-y-2" data-id="element-102">
              <Label htmlFor="status" data-id="element-103">Status</Label>
              <select id="status" value={formData.status} onChange={e => handleChange('status', e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" data-id="element-104">
                <option value="Opener" data-id="element-105">Opener</option>
                <option value="Erstnachricht" data-id="element-106">Erstnachricht</option>
                <option value="Interessiert" data-id="element-107">Interessiert</option>
                <option value="Kunde" data-id="element-108">Kunde</option>
                <option value="Follow-up" data-id="element-109">Follow-up</option>
                <option value="Disqualifiziert" data-id="element-110">Disqualifiziert</option>
              </select>
            </div>

            <div className="space-y-2" data-id="element-111">
              <Label htmlFor="followUp" data-id="element-112">Follow-up Datum</Label>
              <Input id="followUp" value={formData.followUp} onChange={e => handleChange('followUp', e.target.value)} placeholder="DD.MM.YYYY" data-id="element-113" />
            </div>
          </div>

          <div className="space-y-2" data-id="element-114">
            <Label data-id="element-115">Tags</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 rounded-md border border-input bg-background" data-id="element-116">
              {AVAILABLE_TAGS.map(tag => <label key={tag} className="flex items-center gap-2 cursor-pointer group" data-id="element-117">
                  <input type="checkbox" checked={formData.tags.includes(tag)} onChange={() => handleTagToggle(tag)} className="h-4 w-4 rounded border-input accent-[#f88d3e] focus:ring-2 focus:ring-[#f88d3e] focus:ring-offset-2 cursor-pointer" data-id="element-118" />
                  <span className="text-sm text-foreground group-hover:text-[#f88d3e] transition-colors" data-id="element-119">
                    {tag}
                  </span>
                </label>)}
            </div>
            <p className="text-xs text-muted-foreground" data-id="element-120">
              Wählen Sie eine oder mehrere Tags aus
            </p>
          </div>

          <div className="space-y-2" data-id="element-121">
            <Label htmlFor="notes" data-id="element-122">Notizen</Label>
            <textarea id="notes" value={formData.notes} onChange={e => handleChange('notes', e.target.value)} rows={4} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none" placeholder="Fügen Sie Notizen zu diesem Kontakt hinzu..." data-id="element-123" />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border" data-id="element-124">
            <Button type="button" variant="outline" onClick={onClose} data-id="element-125">
              Abbrechen
            </Button>
            <Button type="submit" data-id="element-126">Änderungen speichern</Button>
          </div>
        </form>
      </Card>
    </div>;
}